import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

type Status = "yes" | "partial" | "no" | "roadmap" | "only-us" | "pricing";

interface FeatureRow {
  feature: string;
  velaro: Status;
  velajNote?: string;
  mailchimp: Status;
  mailchimpNote?: string;
  constantContact: Status;
  ccNote?: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const FEATURES: FeatureRow[] = [
  {
    feature: "Drag-and-drop email builder",
    velaro: "yes", mailchimp: "yes", constantContact: "yes",
  },
  {
    feature: "A/B subject line testing",
    velaro: "yes",
    mailchimp: "partial", mailchimpNote: "Paid only",
    constantContact: "partial", ccNote: "Paid only",
  },
  {
    feature: "Live chat + email in one platform",
    velaro: "only-us",
    mailchimp: "no", constantContact: "no",
  },
  {
    feature: "Email sequences (drip)",
    velaro: "yes", mailchimp: "yes", constantContact: "yes",
  },
  {
    feature: "Post-chat email trigger",
    velaro: "only-us",
    mailchimp: "no", constantContact: "no",
  },
  {
    feature: "Verified sender domain (SPF/DKIM/DMARC)",
    velaro: "yes", velajNote: "Per customer",
    mailchimp: "yes", mailchimpNote: "Shared",
    constantContact: "yes", ccNote: "Shared",
  },
  {
    feature: "Auto-suppress hard bounces",
    velaro: "yes", mailchimp: "yes", constantContact: "yes",
  },
  {
    feature: "Spam complaint auto-unsubscribe",
    velaro: "yes", mailchimp: "yes", constantContact: "yes",
  },
  {
    feature: "Bounce rate auto-pause protection",
    velaro: "yes",
    mailchimp: "partial", mailchimpNote: "Manual",
    constantContact: "partial", ccNote: "Manual",
  },
  {
    feature: "Contact segmentation by chat history",
    velaro: "only-us",
    mailchimp: "no", constantContact: "no",
  },
  {
    feature: "Contact segmentation by tags",
    velaro: "yes", mailchimp: "yes", constantContact: "yes",
  },
  {
    feature: "Re-send to non-openers",
    velaro: "yes",
    mailchimp: "yes",
    constantContact: "partial", ccNote: "Paid only",
  },
  {
    feature: "Deliverability health dashboard",
    velaro: "yes", mailchimp: "yes",
    constantContact: "partial", ccNote: "Basic",
  },
  {
    feature: "Monthly volume charts",
    velaro: "yes", mailchimp: "yes", constantContact: "yes",
  },
  {
    feature: "Per-campaign open/click analytics",
    velaro: "yes", mailchimp: "yes", constantContact: "yes",
  },
  {
    feature: "Hourly open time analysis",
    velaro: "partial", velajNote: "Coming",
    mailchimp: "yes",
    constantContact: "partial", ccNote: "Paid",
  },
  {
    feature: "Heatmap / click map",
    velaro: "roadmap",
    mailchimp: "yes", mailchimpNote: "Paid",
    constantContact: "yes", ccNote: "Paid",
  },
  {
    feature: "AI send time optimization",
    velaro: "roadmap",
    mailchimp: "yes", mailchimpNote: "Paid ($350+)",
    constantContact: "no",
  },
  {
    feature: "Landing page builder",
    velaro: "roadmap",
    mailchimp: "yes", mailchimpNote: "Paid",
    constantContact: "yes",
  },
  {
    feature: "Free tier",
    velaro: "partial", velajNote: "Per plan",
    mailchimp: "yes", mailchimpNote: "500 contacts",
    constantContact: "no",
  },
  {
    feature: "Price at 100K sends/month",
    velaro: "pricing", velajNote: "~$25 ACS cost",
    mailchimp: "pricing", mailchimpNote: "$299/mo",
    constantContact: "pricing", ccNote: "$195/mo",
  },
  {
    feature: "Built into live chat platform",
    velaro: "yes",
    mailchimp: "no", constantContact: "no",
  },
  {
    feature: "AI conversation triggers",
    velaro: "only-us",
    mailchimp: "no", constantContact: "no",
  },
];

const MAILCHIMP_COMPLAINTS = [
  {
    pain: "Pricing complexity that inflates bills",
    detail:
      "Mailchimp counts unsubscribed contacts against your list limit — you pay for contacts you can't even email. Upgrading mid-month charges a prorated fee; downgrading doesn't refund it.",
  },
  {
    pain: "Template rigidity",
    detail:
      "The drag-and-drop editor is sandboxed — you can't inject custom HTML or CSS reliably. Many users complain templates look broken on Outlook despite passing preview checks.",
  },
  {
    pain: "Automations are confusing and unreliable",
    detail:
      "The legacy Automation builder and the newer Customer Journeys builder are two separate systems with different rules and no migration path. Users frequently report journeys silently stopping.",
  },
  {
    pain: "Poor customer support",
    detail:
      "Free and Essentials plans have no phone or live chat support — only email tickets with multi-day response times. Critical send failures during a campaign may go unanswered for days.",
  },
  {
    pain: "Contact counting tricks inflate billing",
    detail:
      "Each email address across multiple audiences counts separately. A contact in three lists = three contacts toward your limit. Users routinely discover their bill is 2–3× what they expected.",
  },
];

// ─── Cell rendering ───────────────────────────────────────────────────────────

function StatusCell({ status, note, isVelaro }: { status: Status; note?: string; isVelaro?: boolean }) {
  if (status === "only-us") {
    return (
      <div className="flex flex-col items-center gap-1">
        <span className="text-emerald-600 text-base font-bold">✅</span>
        <span className="text-[10px] bg-emerald-100 text-emerald-700 font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap">
          Only us
        </span>
      </div>
    );
  }
  if (status === "pricing") {
    const isCheap = isVelaro;
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-base">{isCheap ? "💰" : "💸"}</span>
        {note && <span className={`text-[10px] font-semibold ${isCheap ? "text-emerald-700" : "text-orange-600"}`}>{note}</span>}
      </div>
    );
  }
  if (status === "yes") {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-emerald-500 text-base">✅</span>
        {note && <span className="text-[10px] text-slate-500">{note}</span>}
      </div>
    );
  }
  if (status === "partial") {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-amber-400 text-base">⚠️</span>
        {note && <span className="text-[10px] text-amber-600 font-medium">{note}</span>}
      </div>
    );
  }
  if (status === "roadmap") {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-slate-300 text-base">❌</span>
        <span className="text-[10px] text-slate-400">Roadmap</span>
      </div>
    );
  }
  // no
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-red-400 text-base">❌</span>
      {note && <span className="text-[10px] text-slate-400">{note}</span>}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ComparisonChart() {
  const [expanded, setExpanded] = React.useState(false);
  const visibleRows = expanded ? FEATURES : FEATURES.slice(0, 12);

  return (
    <div className="mt-10 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Platform Comparison</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          How Velaro Campaigns stacks up against Mailchimp and Constant Contact.
        </p>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 w-1/2">Feature</th>
                <th className="text-center px-3 py-3 text-xs font-semibold text-emerald-700 bg-emerald-50 w-1/6">
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="text-base">🟢</span>
                    Velaro
                  </div>
                </th>
                <th className="text-center px-3 py-3 text-xs font-medium text-gray-500 w-1/6">Mailchimp</th>
                <th className="text-center px-3 py-3 text-xs font-medium text-gray-500 w-1/6">Constant Contact</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleRows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                  <td className="px-4 py-3 text-xs text-gray-700 font-medium leading-snug">{row.feature}</td>
                  <td className="px-3 py-3 text-center bg-emerald-50/30">
                    <StatusCell status={row.velaro} note={row.velajNote} isVelaro />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <StatusCell status={row.mailchimp} note={row.mailchimpNote} />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <StatusCell status={row.constantContact} note={row.ccNote} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Show more / less toggle */}
        {FEATURES.length > 12 && (
          <div className="border-t border-gray-100 px-4 py-2 text-center">
            <button
              onClick={() => setExpanded(x => !x)}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              {expanded ? `Show less ↑` : `Show all ${FEATURES.length} features ↓`}
            </button>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5"><span className="text-emerald-500">✅</span> Available</span>
        <span className="flex items-center gap-1.5"><span className="text-amber-400">⚠️</span> Partial / Paid-only</span>
        <span className="flex items-center gap-1.5"><span className="text-red-400">❌</span> Not available</span>
        <span className="flex items-center gap-1.5">
          <span className="text-[10px] bg-emerald-100 text-emerald-700 font-semibold px-1.5 py-0.5 rounded-full">Only us</span>
          Velaro-exclusive capability
        </span>
      </div>

      {/* Where Mailchimp users complain */}
      <div className="bg-orange-50 border border-orange-100 rounded-xl p-5 space-y-3">
        <h4 className="text-sm font-semibold text-orange-900">Where Mailchimp users complain</h4>
        <p className="text-xs text-orange-700">
          Common pain points surfaced in G2, Capterra, and Reddit threads — all things Velaro avoids by design.
        </p>
        <div className="space-y-3">
          {MAILCHIMP_COMPLAINTS.map((c, i) => (
            <div key={i} className="flex gap-3">
              <span className="text-orange-400 shrink-0 mt-0.5 text-base">⚡</span>
              <div>
                <p className="text-xs font-semibold text-orange-900">{c.pain}</p>
                <p className="text-xs text-orange-700 mt-0.5 leading-relaxed">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
